import type { PostResumo } from "@/lib/blog.functions";
// Portado de /tmp/astro/src/pages/tratamentos/massa-muscular-sarcopenia.astro (site Astro).
import { Hero } from "@/components/itv/tratamento/Hero";
import { Pilares } from "@/components/itv/tratamento/Pilares";
import { Dores } from "@/components/itv/tratamento/Dores";
import { CardsIlustrado } from "@/components/itv/tratamento/CardsIlustrado";
import { Medico } from "@/components/itv/tratamento/Medico";
import { Faq } from "@/components/itv/tratamento/Faq";
import { Conteudos } from "@/components/itv/tratamento/Conteudos";
import { CtaPratico } from "@/components/itv/tratamento/CtaPratico";

const pilares = [
  { icone: "/icones/search.svg", titulo: "Mede o ponto de partida", texto: "Massa muscular, gordura e força avaliadas na primeira consulta. A balança não mostra do que o peso é feito." },
  { icone: "/icones/shield.svg", titulo: "Três especialidades", texto: "Nutrologia, Medicina do Esporte e Ortopedia no mesmo médico: nutrição, treino e articulação avaliados juntos." },
  { icone: "/icones/video-cam.svg", titulo: "Onde e como", texto: "Presencial em Bauru ou por teleconsulta para todo o Brasil. Atendimento particular." },
];

const sinais = [
  { icone: "/icones/flame.svg", titulo: "Força diminuindo", texto: "Abrir um pote, subir escadas ou carregar compras ficou mais pesado do que era." },
  { icone: "/icones/moon.svg", titulo: "Peso igual, corpo diferente", texto: "A balança estável pode esconder troca de músculo por gordura. A composição corporal mostra." },
  { icone: "/icones/activity.svg", titulo: "Treino sem resposta", texto: "Meses de academia sem evolução costumam ter causa: proteína insuficiente, sono, deficiências ou fatores hormonais." },
  { icone: "/icones/clipboard.svg", titulo: "Emagrecimento levando músculo", texto: "Perda de peso rápida, principalmente com medicação, derruba massa magra quando não há acompanhamento." },
];

const plano = [
  { img: "/img/musculo/conduta-1.webp", titulo: "Ajuste de proteína", texto: "A quantidade certa para o seu peso, idade e treino, distribuída ao longo do dia." },
  { img: "/img/musculo/conduta-2.webp", titulo: "Orientação de treino", texto: "O estímulo que o músculo precisa, em parceria com quem prescreve o seu treino." },
  { img: "/img/musculo/conduta-3.webp", titulo: "Suplementação", texto: "Creatina tem boa evidência para força e massa. O restante entra apenas com indicação." },
  { img: "/img/musculo/conduta-4.webp", titulo: "Correção de deficiências", texto: "Vitamina D, hormônios e outros fatores que limitam a resposta muscular." },
  { img: "/img/musculo/conduta-5.webp", titulo: "Proteção no emagrecimento", texto: "Para quem usa canetas ou está em dieta: perder gordura preservando músculo." },
  { img: "/img/musculo/conduta-6.webp", titulo: "Sono e recuperação", texto: "O músculo cresce na recuperação. Sono ruim limita o resultado do treino." },
];

// Menção a "Hospital Albert Einstein" omitida: não consta na lista de credenciais autorizadas.
const paragrafosMedico = [
  "Para tratar músculo, as três especialidades do Dr. Thiago trabalham juntas: Nutrologia, Medicina do Esporte e Ortopedia.",
  "Nutrição, treino e articulação avaliados pelo mesmo médico, na mesma consulta. Formação pela UNIFESP-EPM, em 2010.",
];

export const faq = [
  { pergunta: "Como saber se estou perdendo massa muscular?", resposta: "Força diminuindo em tarefas comuns e mudança na composição corporal com peso estável são os principais sinais. A bioimpedância confirma." },
  { pergunta: "Dá para ganhar massa muscular depois dos 60?", resposta: "Sim. O músculo responde ao estímulo em qualquer idade. O que muda é a estratégia: a dose de proteína, o tipo de treino e o tempo de recuperação são ajustados ao caso." },
  { pergunta: "Quanta proteína eu preciso?", resposta: "Depende do seu peso, da sua idade e do seu treino. Mais importante do que o total é distribuir a proteína ao longo do dia, e isso é definido na consulta." },
  { pergunta: "Creatina funciona?", resposta: "Creatina tem boa evidência para força e massa muscular. As demais suplementações entram apenas quando há indicação no seu caso." },
  { pergunta: "Funciona por teleconsulta?", resposta: "Sim. A avaliação e o acompanhamento acontecem por vídeo, com os exames feitos na sua cidade, para todo o Brasil." },
];


export function MassaMuscular({ artigos }: { artigos: PostResumo[] }) {
  return (
    <>
      <Hero
        trilha={[{ nome: "Home", href: "/" }, { nome: "Tratamentos", href: "/tratamentos/" }, { nome: "Massa muscular e sarcopenia" }]}
        sobretitulo="TRATAMENTO"
        linha1="Massa muscular"
        linha2="e sarcopenia."
        chamada="A perda de massa muscular começa cedo e avança devagar. Tem tratamento, e o corpo responde em qualquer idade."
        imagem="/img/musculo/hero.webp"
      />

      <Pilares
        sobretitulo="TRATAMENTO DA SARCOPENIA"
        titulo="Ganhar músculo começa por medir"
        chamada="O tratamento avalia massa e função muscular com bioimpedância, avaliação de força e exames. A partir disso, o plano combina alimentação, orientação de treino e correção de deficiências, com reavaliações periódicas para acompanhar a evolução em números."
        itens={pilares}
      />

      <Dores
        sobretitulo="SINAIS DE SARCOPENIA"
        titulo="Sinais de perda de massa muscular"
        chamada="A sarcopenia é reconhecida como doença e costuma passar despercebida no início. Estes são os sinais mais comuns:"
        itens={sinais}
      />

      <CardsIlustrado
        sobretitulo="O PLANO MUSCULAR"
        titulo="Proteína, treino e o que mais o músculo precisa"
        chamada="Conforme a avaliação de força e de composição corporal:"
        cards={plano}
      />

      <Medico paragrafos={paragrafosMedico} />
      <Faq titulo="Dúvidas sobre massa muscular e sarcopenia" itens={faq} />
      <Conteudos titulo="Continue lendo sobre músculo e treino" artigos={artigos} />
      <CtaPratico linha1="Comece seu tratamento de" linha2="massa muscular. Escolha o formato." />
    </>
  );
}
